// ============================================================
//  ADMIN API — ADMIN DASHBOARD
//  Path: src/api/admin.api.ts
//
//  Pure HTTP calls — maps 1:1 to backend admin.routes.js
//  Backend base path: /api/admin
//
//  Returns raw axios response to keep existing page code working.
//  Pages access: res.data.data, res.data.pagination, etc.
//
//  ┌───────────────────────────────────────────────────────────┐
//  │  Frontend (admin.api.ts)      →  Backend (admin.routes)   │
//  │───────────────────────────────────────────────────────────│
//  │  getDashboardStatsApi()       →  GET    /stats            │
//  │  listUsersApi()               →  GET    /users            │
//  │  getUserByIdApi()             →  GET    /users/:id        │
//  │  toggleUserActiveApi()        →  PATCH  /users/:id/active │
//  │  resetUserPasswordApi()       →  PATCH  /users/:id/password│
//  │  listExpertsApi()             →  GET    /experts          │
//  │  updateExpertStatusApi()      →  PATCH  /experts/:id/status│
//  │  approveCompanyApi()          →  PATCH  /companies/:id/approve│
//  │  revokeCompanyApprovalApi()   →  PATCH  /companies/:id/revoke-approval│
//  │  deleteCompanyApi()           →  DELETE /companies/:id     │
//  │  listCompanyWorkerDirectoryApi() → GET  /company-workers   │
//  │  createAdminApi()             →  POST   /admins           │
//  │  listAdminsApi()              →  GET    /admins           │
//  │  updateAdminRoleApi()         →  PATCH  /admins/:id/role  │
//  │  removeAdminApi()             →  DELETE /admins/:id       │
//  │  upsertCategoryInsuranceFeeApi() → PUT  /categories/:id/insurance-fees/:insuranceType │
//  │  deleteCategoryInsuranceFeeApi() → DELETE /categories/:id/insurance-fees/:insuranceType │
//  │  listInsuranceDefaultsApi()   →  GET    /insurance-defaults │
//  │  setInsuranceDefaultApi()     →  PUT    /insurance-defaults/:insuranceType │
//  │  resetCategoryInsuranceFeeToDefaultApi() → POST /categories/:id/insurance-fees/:insuranceType/reset-to-default │
//  │  listExpertInsurancePoliciesApi() → GET /experts/:expertId/insurance-policies │
//  │  verifyExpertInsurancePolicyApi() → PATCH /insurance-policies/:policyId/verify │
//  │  updateExpertInsurancePolicyExpiryApi() → PATCH /insurance-policies/:policyId/expiry │
//  └───────────────────────────────────────────────────────────┘
//
//  ADDED (quarantine/scanner migration pass): generateAdMediaUploadUrlApi
//  / confirmAdMediaUploadApi → POST /advertisements/upload-url and
//  POST /advertisements/upload-url/confirm. Replaces the old direct-
//  to-Firebase advertisement media upload in AdvertisementsPage.tsx.
//
//  FIX (review pass) — every call below was checked against the
//  backend's admin.routes.js; all of them map to a real route.
//  - Slow calls get their own time limit instead of config/api.ts's
//    20s default: PDFs (task + financial report) 60s, rescan 90s (the
//    scanner service can cold-start). Previously a big PDF or a rescan
//    could be reported as "check your connection" while the server was
//    still working — and for rescan, the file really was re-queued.
//  - Path segments are URL-encoded (seg()). IDs are cuids, but
//    appType / policyType / vehicleType / insuranceType / fileId are
//    plain strings; a '/', '?' or '#' in one would hit a different
//    route or drop part of the path.
//  - updateCategoryReachApi now calls PATCH /categories/:id/reach,
//    the route built for it. The generic PATCH /categories/:id hands
//    these fields to the same code, so behaviour is unchanged — it's
//    just direct now, with its own audit-log action.
// ============================================================

import api from '../config/api'

// Encodes one URL path segment.
const seg = (value: string) => encodeURIComponent(value)

// Per-request time limits for calls that legitimately take longer than
// the 20s default in config/api.ts.
const PDF_TIMEOUT_MS    = 60_000
const RESCAN_TIMEOUT_MS = 90_000

// ── Dashboard ────────────────────────────────────────────────
export const getDashboardStatsApi = () => api.get('/api/admin/stats')

// ── Users ────────────────────────────────────────────────────
export const listUsersApi = (params?: {
  page?: number; limit?: number; search?: string; role?: string; isActive?: string
  // 'true' → only accounts with an open issue (deactivated, a failed
  // charge, an open dispute) — see admin.service.js's
  // getCustomerIdsWithAnyIssue. Omit or any other value = no filter.
  issuesOnly?: string
  // 'true' → only accounts awaiting an admin decision — see
  // admin.service.js's buildCustomerNeedsAction. Always empty today
  // (no customer approval workflow exists), kept for parity with
  // listExpertsApi's needsActionOnly. Omit or any other value = no filter.
  needsActionOnly?: string
}) => api.get('/api/admin/users', { params })

export const getUserByIdApi = (id: string) => api.get(`/api/admin/users/${seg(id)}`)

export const toggleUserActiveApi = (id: string, isActive: boolean) =>
  api.patch(`/api/admin/users/${seg(id)}/active`, { isActive })

export const resetUserPasswordApi = (id: string, newPassword: string) =>
  api.patch(`/api/admin/users/${seg(id)}/password`, { newPassword })

// Permanently deletes the user and ALL their data — irreversible.
// super_admin only; requires recent re-auth on the backend.
export const deleteUserApi = (id: string) =>
  api.delete(`/api/admin/users/${seg(id)}`)

// "Clear" / "Restore" on the Customer Detail page's Issues panel.
// dismiss hides that one issue code until either an admin restores it
// or a NEW occurrence of the same issue happens (see admin.service.js's
// IssueDismissal comment) — fixing the underlying problem still makes
// it disappear on its own with no action needed here.
export const dismissUserIssueApi = (userId: string, issueCode: string) =>
  api.post(`/api/admin/users/${seg(userId)}/issues/${seg(issueCode)}/dismiss`)

export const restoreUserIssueApi = (userId: string, issueCode: string) =>
  api.delete(`/api/admin/users/${seg(userId)}/issues/${seg(issueCode)}/dismiss`)

// "Show dismissed" list — every issue ever cleared on this account.
export const listUserIssueDismissalsApi = (userId: string) =>
  api.get(`/api/admin/users/${seg(userId)}/issues/dismissed`)

// ── Experts ──────────────────────────────────────────────────
export const listExpertsApi = (params?: {
  page?: number; limit?: number; status?: string
  // 'true' → only taskers who have not answered the GST/HST question.
  taxNotDeclared?: string
  // 'true' → approved taskers still finishing payout (Stripe) and/or GST/HST.
  setupPending?: string
  // 'true' → only experts with an open issue (suspended/rejected,
  // failed background check, rejected documents, subscription past
  // due, failed payouts, open disputes) — see admin.service.js's
  // getExpertIdsWithAnyIssue. Omit or any other value = no filter.
  issuesOnly?: string
  // 'true' → only experts awaiting an admin decision (pending/
  // under_review approval, documents submitted awaiting certification)
  // — see admin.service.js's getExpertIdsNeedingAction. Deliberately
  // separate from issuesOnly: nothing is wrong here, a decision is
  // just outstanding. Omit or any other value = no filter.
  needsActionOnly?: string
}) => api.get('/api/admin/experts', { params })

// One expert by their Expert id. ExpertDetailPage previously pulled
// the whole list and filtered locally, which quietly stopped working
// past the first 100 experts (the backend clamps limit to 100).
// Everyone on the way right now (the "on my way" bars). Read-only; distances only.
export const listActiveTripsApi = (params?: { expertId?: string; limit?: number }) =>
  api.get('/api/admin/trips', { params })

// What happened after approval: dated events + where the tasker stands now.
export const getExpertSetupTimelineApi = (expertId: string) =>
  api.get(`/api/admin/experts/${seg(expertId)}/setup-timeline`)

export const getExpertByIdApi = (expertId: string) =>
  api.get(`/api/admin/experts/${seg(expertId)}`)

// "Clear" / "Restore" on the Expert Detail page's Issues panel — see
// dismissUserIssueApi above for the exact behaviour (same pattern,
// keyed by Expert id here instead of User id).
export const dismissExpertIssueApi = (expertId: string, issueCode: string) =>
  api.post(`/api/admin/experts/${seg(expertId)}/issues/${seg(issueCode)}/dismiss`)

export const restoreExpertIssueApi = (expertId: string, issueCode: string) =>
  api.delete(`/api/admin/experts/${seg(expertId)}/issues/${seg(issueCode)}/dismiss`)

// "Show dismissed" list — every issue ever cleared on this account.
export const listExpertIssueDismissalsApi = (expertId: string) =>
  api.get(`/api/admin/experts/${seg(expertId)}/issues/dismissed`)

export const listCompaniesApi = (params?: {
  page?: number; limit?: number
}) => api.get('/api/admin/companies', { params })

// Distinct from updateExpertStatusApi (the owner's personal
// expert-identity approval) and verifyDocumentApi (per-document
// review) — this is the company's own explicit final sign-off.
export const approveCompanyApi = (companyId: string) =>
  api.patch(`/api/admin/companies/${seg(companyId)}/approve`)

// The undo for the above — an admin changing their mind after
// approving a company (fraud discovered, a document turns out to
// have lapsed, etc.). Requires a reason, same as rejecting an
// individual expert below.
export const revokeCompanyApprovalApi = (companyId: string, reason: string) =>
  api.patch(`/api/admin/companies/${seg(companyId)}/revoke-approval`, { reason })

// Fully removes the company account: cancels active hires (customers
// refunded and notified), clears the worker roster, deletes the company's
// documents, and resets the account back to an individual tasker. The
// person's account itself is untouched and they can register as a
// company again later. Irreversible — the backend gates this behind a
// recent re-auth check on top of the normal role check.
export const deleteCompanyApi = (companyId: string, reason: string) =>
  api.delete(`/api/admin/companies/${seg(companyId)}`, { data: { reason } })

// Manual retry for the individual→business Stripe Connect account
// switch approveCompanyApi triggers automatically and best-effort
// (see payment.service.js's ensureCorrectStripeAccountEntityType,
// admin.service.js's approveCompany / retryCompanyStripeAccountSwitch).
// Safe to call any time — a no-op ({ switched: false }) unless the
// company is approved, incorporated/partnership, and not already on
// a business account. Backend: admin.routes.js's PATCH
// /companies/:id/retry-stripe-account-switch.
export const retryCompanyStripeAccountSwitchApi = (companyId: string) =>
  api.patch(`/api/admin/companies/${seg(companyId)}/retry-stripe-account-switch`)

// Directory of individuals on company rosters platform-wide, filterable
// by the WORKER's own address (not the company's). All params optional —
// omit all three to list every roster worker across every company.
export const listCompanyWorkerDirectoryApi = (params?: {
  country?: string; province?: string; city?: string; page?: number; limit?: number
}) => api.get('/api/admin/company-workers', { params })

// ── Worker-initiated roster join requests (admin override) ─────
// See admin.routes.js's own comment on these two routes — a worker
// can now request to join a company's roster themselves (instead of
// only waiting to be invited), and admin gets the same accept/decline
// override every other two-party review in this dashboard has, for
// when the company is slow to respond.
export const listPendingCompanyJoinRequestsApi = () =>
  api.get('/api/admin/company-join-requests')

export const respondToCompanyJoinRequestApi = (companyWorkerId: string, accept: boolean) =>
  api.post(`/api/admin/company-join-requests/${seg(companyWorkerId)}/respond`, { accept })

export const updateExpertStatusApi = (id: string, data: {
  status: string; reason?: string
}) => api.patch(`/api/admin/experts/${seg(id)}/status`, data)

// ── Background Checks ────────────────────────────────────────
export const reviewBackgroundCheckApi = (id: string, data: {
  approved: boolean; reason?: string
}) => api.patch(`/api/admin/experts/${seg(id)}/background-check-review`, data)

export const listPendingBackgroundChecksApi = () => api.get('/api/admin/background-checks/pending')
export const listPastDueSubscriptionExpertsApi = () => api.get('/api/admin/experts/past-due-subscriptions')

export const listExpertsPendingCertificationApi = () => api.get('/api/admin/experts/pending-certification')

// These three all go through admin.routes.js (mounted at
// /api/admin), NOT category.routes.js (mounted at /api/categories,
// used by the customer/expert mobile apps for task creation and
// profile category selection). The two route files implement
// parallel, separate category-management logic — admin.routes.js
// is the one actually built for this dashboard: its GET already
// includes isActive on every row, and its /pickup-flags endpoint
// is the only one that enforces the isTwoLocation <->
// requiresPickupCode dependency rule (see admin.service.js).
export const listAllCategoriesAdminApi = () => api.get('/api/admin/categories')

// Deliberately calls category.routes.js (NOT admin.routes.js like
// every other category function above) — this is the one shared,
// single-source-of-truth endpoint all three apps (expert,
// customer, admin) call for group display order, backed by the
// CategoryGroup table (see schema.prisma). Unlike category
// listing itself, there was never a reason to build a second,
// admin-specific parallel version of this one — no admin role is
// required, no admin-only fields are involved, it's just an
// ordered list of group name strings, identical for every caller.
export const getCategoryGroupOrderApi = () => api.get('/api/categories/groups')

export const removeExpertCategoryApi = (expertId: string, categoryId: string) =>
  api.delete(`/api/admin/experts/${seg(expertId)}/categories/${seg(categoryId)}`)

export const updateCategoryApi = (categoryId: string, data: {
  // Communication — replaces old callType enum
  voiceCallPostHireEnabled?:     boolean
  videoCallPostHireEnabled?:     boolean
  voiceCallPreHireEnabled?:      boolean
  videoCallPreHireEnabled?:      boolean
  chatEnabled?:                  boolean
  callTimeLimitMinutes?:         number | null
  voiceCallPreHireLimitMinutes?:  number | null
  videoCallPreHireLimitMinutes?:  number | null
  voiceCallPostHireLimitMinutes?: number | null
  videoCallPostHireLimitMinutes?: number | null
  voiceCallPreHireMaxCalls?:     number | null
  videoCallPreHireMaxCalls?:     number | null
  voiceCallPostHireMaxCalls?:    number | null
  videoCallPostHireMaxCalls?:    number | null
  // Everything else unchanged
  isActive?: boolean; isInHome?: boolean; requiresLicense?: boolean
  requiresBackgroundCheck?: boolean
  requiresVulnerableSectorCheck?: boolean
  allowsCustomerAddress?: boolean; allowsExpertLocation?: boolean; allowsVirtual?: boolean
  commissionExempt?: boolean; allowsZeroPrice?: boolean; commissionPercent?: number | null
  serviceFeeExempt?: boolean; serviceFeeType?: 'percent' | 'flat' | null; serviceFeeValue?: number | null
  serviceFeeCustomerPct?: number; serviceFeeTaskerPct?: number
  processingFeeExempt?: boolean; processingFeeType?: 'percent' | 'flat' | null; processingFeeValue?: number | null
  processingFeeCustomerPct?: number; processingFeeTaskerPct?: number
  allowsHourlyPricing?: boolean; allowsFlatPricing?: boolean; allowsFreeToConnect?: boolean
  vehicleRequirement?: 'any' | 'motorized' | 'car_van' | 'van_truck'
  insuranceFeeExempt?: boolean
  // FIX (production-readiness pass): these two were missing here even
  // though admin.service.ts's updateCategory and CategoriesPage.tsx's
  // handleLiabilityInsuranceToggle already send them — the calls work
  // at runtime regardless (JS doesn't strip untyped object properties),
  // but the type gap meant nothing here would catch a real mistake.
  requiresLiabilityInsurance?: boolean
  minLiabilityCoverageAmount?: number | null
  // 'manual' = fee rows; 'insurer' = live price from the connected insurer
  insurancePricing?: 'manual' | 'insurer'
  // Task reach + availability count + escalation
  taskReach?:                  'local' | 'radius' | 'global'
  maxTaskRadiusKm?:            number | null
  allowCustomerToExpandReach?: boolean
  showAvailabilityCount?:      'none' | 'total' | 'online'
  escalationEnabled?:          boolean
  escalationStep1Minutes?:     number | null
  escalationStep2Minutes?:     number | null
  escalationStep3Minutes?:     number | null
}) => api.patch(`/api/admin/categories/${seg(categoryId)}`, data)

// Separate endpoint (not part of updateCategoryApi above) because the
// backend enforces a dependency rule between these two flags:
//   — turning on requiresPickupCode auto-enables isTwoLocation
//   — turning off isTwoLocation auto-disables requiresPickupCode
// See admin.service.js -> updateCategoryPickupFlags on the backend.
export const updateCategoryPickupFlagsApi = (categoryId: string, data: {
  isTwoLocation?: boolean; requiresPickupCode?: boolean
}) => api.patch(`/api/admin/categories/${seg(categoryId)}/pickup-flags`, data)

export const overrideExpertSubscriptionApi = (expertId: string, data: {
  subscriptionStatus: string; subscriptionRenewsAt?: string; reason?: string
}) => api.patch(`/api/admin/experts/${seg(expertId)}/subscription`, data)

// ── Vehicle Verification Review ───────────────────────────────
export const listPendingVehicleVerificationsApi = () => api.get('/api/admin/vehicles/pending')

export const reviewVehicleApi = (vehicleId: string, data: {
  approved: boolean; rejectionReason?: string
}) => api.patch(`/api/admin/vehicles/${seg(vehicleId)}/review`, data)

// ── Tasks — full monitoring (payments, pairs, timing) ─────────
export const listTasksApi = (params?: {
  page?: number; limit?: number; status?: string; paymentStatus?: string; categoryId?: string; search?: string
}) => api.get('/api/admin/tasks', { params })

export const getTaskDetailApi = (id: string) => api.get(`/api/admin/tasks/${seg(id)}`)

export const updateTaskApi = (id: string, data: {
  title?: string; description?: string; area?: string; address?: string; budget?: number
}) => api.patch(`/api/admin/tasks/${seg(id)}`, data)

export const deleteTaskApi = (id: string) => api.delete(`/api/admin/tasks/${seg(id)}`)

// ── Task timeline & activity tracks ───────────────────────────
// Every recorded action with who / when, and for location actions the
// GPS, the distance to the address and whether it was verified.
export const getTaskTimelineApi     = (taskId: string)     => api.get(`/api/admin/tasks/${seg(taskId)}/timeline`)
export const getCustomerTimelineApi = (customerId: string) => api.get(`/api/admin/customers/${seg(customerId)}/timeline`)
export const getExpertTimelineApi   = (expertId: string)   => api.get(`/api/admin/experts/${seg(expertId)}/timeline`)

// ── Resolve a disputed task (super_admin / admin) ─────────────
// resolution: 'pay_expert' | 'refund_customer' | 'tasker_no_show'
export const resolveDisputeApi = (taskId: string, data: { resolution: string; note?: string }) =>
  api.post(`/api/admin/tasks/${seg(taskId)}/resolve-dispute`, data)

// ── Mark as Done on the tasker's behalf (super_admin / admin) ──
// The customer still confirms or disputes.
export const markTaskDoneForTaskerApi = (taskId: string, data: { note?: string }) =>
  api.post(`/api/admin/tasks/${seg(taskId)}/mark-done`, data)

export const getTaskPdfApi = (id: string) =>
  api.get(`/api/admin/tasks/${seg(id)}/pdf`, { responseType: 'blob', timeout: PDF_TIMEOUT_MS })

export const getFinancialReportApi = (params: { scope: string; id?: string; period: string }) =>
  api.get('/api/admin/reports/financial', { params })

export const getFinancialReportPdfApi = (params: { scope: string; id?: string; period: string }) =>
  api.get('/api/admin/reports/financial/pdf', { params, responseType: 'blob', timeout: PDF_TIMEOUT_MS })

export type TaxReportParams = {
  from?: string; to?: string; province?: string; component?: string
  entryType?: string; expertId?: string; taskId?: string; page?: number; limit?: number
}

export const getTaxLedgerReportApi = (params: TaxReportParams) =>
  api.get('/api/admin/reports/tax', { params })

export const getTaxProvinceRatesApi = () =>
  api.get('/api/admin/tax/provinces')

export const getTaxLedgerCsvApi = (params: TaxReportParams) =>
  api.get('/api/admin/reports/tax/csv', { params, responseType: 'blob', timeout: PDF_TIMEOUT_MS })

// Task coverage & payouts report: who insures the worker, who was hired,
// how the money is split. Read-only; super_admin / admin.
export type TaskCoverageParams = {
  from?: string; to?: string; taskStatus?: string; paymentStatus?: string
  coveredBy?: string; hiredBy?: string; companyId?: string; workerId?: string
  search?: string; page?: number; limit?: number
}

export const getTaskCoverageReportApi = (params: TaskCoverageParams) =>
  api.get('/api/admin/reports/task-coverage', { params })

export const getTaskCoverageCsvApi = (params: TaskCoverageParams) =>
  api.get('/api/admin/reports/task-coverage/csv', { params, responseType: 'blob', timeout: PDF_TIMEOUT_MS })

export const getTaxLedgerPdfApi = (params: TaxReportParams) =>
  api.get('/api/admin/reports/tax/pdf', { params, responseType: 'blob', timeout: PDF_TIMEOUT_MS })

export const getNegotiationForAdminApi = (applicationId: string) =>
  api.get(`/api/admin/applications/${seg(applicationId)}/negotiation`)

// Read-only — write access is expert-only via the customer/expert
// apps, never from the admin dashboard. See sessionNote.routes.js
// on the backend (mounted separately from admin.routes.js, but
// this dashboard-facing call goes through the admin-gated mirror
// route at /api/admin/applications/:applicationId/session-notes).
export const getSessionNotesForAdminApi = (applicationId: string) =>
  api.get(`/api/admin/applications/${seg(applicationId)}/session-notes`)

export const listReviewsForUserApi = (userId: string) =>
  api.get(`/api/admin/users/${seg(userId)}/reviews`)

export const listExpertDocumentsApi = (expertId: string) =>
  api.get(`/api/admin/experts/${seg(expertId)}/documents`)

// Architecture doc section 12 — the audited, MFA-gated single-document
// view link. Distinct from listExpertDocumentsApi above (which still
// returns doc.fileUrl directly and is left untouched for anywhere else
// that reads the list): this is called on demand, per document, right
// before a preview is opened, and every call is written to the audit
// log on the backend BEFORE the link is issued — a failed audit write
// means no link comes back at all. Backend: admin.routes.js's
// GET /experts/:id/documents/:docId/view-url (requireMfa + a 12-hour
// requireRecentAuth window on top of the normal role check). A 401
// here means the signed-in session has no authenticator-app second
// factor on it yet — see ExpertDetailPage.tsx's openDocumentPreview
// for how that's surfaced to the admin.
export const getDocumentViewUrlApi = (expertId: string, docId: string) =>
  api.get(`/api/admin/experts/${seg(expertId)}/documents/${seg(docId)}/view-url`)

// ── Onboarding status (the resolver) ─────────────────────────
export const getExpertOnboardingStatusApi = (expertId: string) =>
  api.get(`/api/admin/experts/${seg(expertId)}/onboarding-status`)

// insurancePolicy is only present when admin.service.js's verifyDocument
// requires it (approving a liability_insurance / commercial_auto_insurance
// / insurance_proof document) — passed straight through in the body
// alongside isVerified/reason. Previously this function's signature
// didn't accept it at all, so ExpertDetailPage.tsx and CompaniesPage.tsx
// could collect provider/expiry on screen and it would still never
// reach the server — the request went out with only isVerified/reason,
// silently dropping the rest.
export const verifyDocumentApi = (
  expertId: string,
  docId: string,
  isVerified: boolean,
  reason?: string,
  insurancePolicy?: { insuranceType?: string; provider: string; expiresAt: string | null }
) =>
  api.patch(`/api/admin/experts/${seg(expertId)}/documents/${seg(docId)}/verify`, { isVerified, reason, insurancePolicy })

export const deleteExpertDocumentApi = (expertId: string, docId: string) =>
  api.delete(`/api/admin/experts/${seg(expertId)}/documents/${seg(docId)}`)

export const removeExpertVehicleApi = (expertId: string, vehicleType: string) =>
  api.delete(`/api/admin/experts/${seg(expertId)}/vehicles/${seg(vehicleType)}`)

export const certifyExpertApi = (expertId: string, certified: boolean) =>
  api.patch(`/api/admin/experts/${seg(expertId)}/certify`, { certified })

// ── Drill-down: tasks for a specific customer/expert ──────────
export const listTasksForCustomerApi = (customerId: string) => api.get(`/api/admin/customers/${seg(customerId)}/tasks`)
export const listTasksForExpertApi = (expertId: string) => api.get(`/api/admin/experts/${seg(expertId)}/tasks`)
export const getExpertEarningsApi = (expertId: string) => api.get(`/api/admin/experts/${seg(expertId)}/earnings`)
export const getCustomerPaymentHistoryApi = (customerId: string) => api.get(`/api/admin/customers/${seg(customerId)}/payment-history`)

// ── Payments & Earnings summary ───────────────────────────────
export const getPaymentsSummaryApi = () => api.get('/api/admin/payments/summary')

// ── Transaction History (every attempt, success or failure) ───
export const listPaymentTransactionsApi = (params?: {
  page?: number; limit?: number; status?: string; type?: string; taskId?: string
}) => api.get('/api/admin/payments/transactions', { params })

export const listTransactionsForTaskApi = (taskId: string) => api.get(`/api/admin/tasks/${seg(taskId)}/transactions`)

export const retryFailedRefundApi = (transactionId: string) =>
  api.post(`/api/admin/payments/transactions/${seg(transactionId)}/retry-refund`)

export const refundExpertSubscriptionApi = (transactionId: string) =>
  api.post(`/api/admin/payments/transactions/${seg(transactionId)}/refund-subscription`)

// ── Admin Management ─────────────────────────────────────────
export const createAdminApi = (data: {
  email: string; password: string; firstName: string; lastName: string; role: string
}) => api.post('/api/admin/admins', data)

export const listAdminsApi = () => api.get('/api/admin/admins')

export const updateAdminRoleApi = (id: string, role: string) =>
  api.patch(`/api/admin/admins/${seg(id)}/role`, { role })

export const removeAdminApi = (id: string) => api.delete(`/api/admin/admins/${seg(id)}`)

// ── CategoryInsuranceFee management (fee split model) ─────────
// Per-type insurance fee rows for a category. Each row has its own
// rate, customer/tasker split, and waiver rule. Admin creates or
// updates via upsert (one row per insuranceType per category); the
// backend enforces customerPct + taskerPct === 100.
export const upsertCategoryInsuranceFeeApi = (
  categoryId: string,
  insuranceType: string,
  data: {
    feeType:          'percent' | 'flat'
    feeValue:         number
    customerPct?:     number
    taskerPct?:       number
    waivedIfVerified?: boolean
    coverageScope?:   string | null
    sortOrder?:       number
  }
) => api.put(`/api/admin/categories/${seg(categoryId)}/insurance-fees/${seg(insuranceType)}`, data)

export const deleteCategoryInsuranceFeeApi = (categoryId: string, insuranceType: string) =>
  api.delete(`/api/admin/categories/${seg(categoryId)}/insurance-fees/${seg(insuranceType)}`)

// ── Platform-wide default insurance fees ──────────────────────
// One default per insurance type. Saving one copies it into every category
// row of that type that has no value of its own; a category edited by hand
// keeps its value until reset.
export const listInsuranceDefaultsApi = () => api.get('/api/admin/insurance-defaults')

export const setInsuranceDefaultApi = (
  insuranceType: string,
  data: {
    feeType:           'percent' | 'flat'
    feeValue:          number
    customerPct?:      number
    taskerPct?:        number
    waivedIfVerified?: boolean
    coverageScope?:    string | null
  }
) => api.put(`/api/admin/insurance-defaults/${seg(insuranceType)}`, data)

export const resetCategoryInsuranceFeeToDefaultApi = (categoryId: string, insuranceType: string) =>
  api.post(`/api/admin/categories/${seg(categoryId)}/insurance-fees/${seg(insuranceType)}/reset-to-default`)

// ── CategoryLicenseRequirement management ─────────────────────
// A category's licence checklist. insuranceType (optional) is what
// admin.service.js's verifyDocument (backend) reads to resolve which
// InsuranceType a document uploaded against this requirement proves —
// set here, once, at category-configuration time, rather than left
// for an admin to guess from the requirement's label at the moment
// they review a document. See MatchNBook_Fee_Split_Strategy.docx
// §7.6/7.7 for why this exists.
export const listCategoryLicenseRequirementsApi = (categoryId: string) =>
  api.get(`/api/admin/categories/${seg(categoryId)}/license-requirements`)

export const createCategoryLicenseRequirementApi = (
  categoryId: string,
  data: {
    docType:        string
    label:          string
    description?:   string | null
    insuranceType?: string | null
    // 'individual' | 'company' — see LicenseVerificationLevel (types/index.ts).
    // Omitted/undefined falls back to the backend's own default ('individual').
    verificationLevel?: string
    isOptional?: boolean
  }
) => api.post(`/api/admin/categories/${seg(categoryId)}/license-requirements`, data)

export const updateCategoryLicenseRequirementApi = (
  categoryId: string,
  requirementId: string,
  data: {
    docType?:       string
    label?:         string
    description?:   string | null
    insuranceType?: string | null
    verificationLevel?: string
    isOptional?: boolean
  }
) => api.patch(`/api/admin/categories/${seg(categoryId)}/license-requirements/${seg(requirementId)}`, data)

// Backend refuses (409) if any document is already uploaded against
// this requirement — see adminService.deleteCategoryLicenseRequirement.
export const deleteCategoryLicenseRequirementApi = (categoryId: string, requirementId: string) =>
  api.delete(`/api/admin/categories/${seg(categoryId)}/license-requirements/${seg(requirementId)}`)

// ── Expert insurance policy admin management ──────────────────
// A verified, in-window policy waives the matching CategoryInsuranceFee
// row for both customer and tasker when waivedIfVerified is true.

// List all policies for one expert — includes unverified ones so
// admin can see what's pending verification.
export const listExpertInsurancePoliciesApi = (expertId: string) =>
  api.get(`/api/admin/experts/${seg(expertId)}/insurance-policies`)

// Verify (approve) or reject an expert's insurance policy.
// verified: true → policy counts for waiver checks.
// verified: false → policy is rejected; any existing waiver on
// future hires will not apply until a new policy is verified.
export const verifyExpertInsurancePolicyApi = (
  policyId: string,
  data: { verified: boolean; note?: string }
) => api.patch(`/api/admin/insurance-policies/${seg(policyId)}/verify`, data)

// Update the expiry date of an existing policy — useful when an
// expert renews and admin updates the record without forcing a
// full re-upload (e.g. policy number unchanged, only end date moved).
export const updateExpertInsurancePolicyExpiryApi = (
  policyId: string,
  data: { expiresAt: string | null; note?: string }
) => api.patch(`/api/admin/insurance-policies/${seg(policyId)}/expiry`, data)

// Delete one policy row. The backend refuses (409) when a past job's fee
// record uses it — reject the policy instead in that case.
export const deleteExpertInsurancePolicyApi = (policyId: string) =>
  api.delete(`/api/admin/insurance-policies/${seg(policyId)}`)
// ── App Version Control ───────────────────────────────────────
export const listAppVersionsApi = () =>
  api.get('/api/admin/app-versions')

export const updateAppVersionApi = (
  appType: string,
  data: {
    minimumVersion?: string; latestVersion?: string
    // Whole numbers; null clears the check. Omit to leave as saved.
    minimumBuild?: number | null; latestBuild?: number | null
  }
) => api.patch(`/api/admin/app-versions/${seg(appType)}`, data)

// ── Policy Version Control ────────────────────────────────────
export const listPolicyVersionsApi = () =>
  api.get('/api/admin/policy-versions')

export const updatePolicyVersionApi = (
  policyType: string,
  data: { version?: string; effectiveDate?: string }
) => api.patch(`/api/admin/policy-versions/${seg(policyType)}`, data)

// ── Platform Fee Settings ───────────────────────────────────────
// Admin-editable overrides for the platform-wide fee defaults
// (commission %, service fee, processing fee, legacy insurance %,
// tax rate) — previously only settable in Secret Manager. A field
// left out of the PATCH body is untouched; a field explicitly sent
// as null clears that override and reverts it to the static
// default. See PlatformSettingsPage.tsx.
export const getPlatformFeeSettingsApi = () =>
  api.get('/api/admin/platform-fee-settings')

export const updatePlatformFeeSettingsApi = (data: {
  platformFeePercent?: number | null
  insuranceFeePercent?: number | null
  serviceFeeType?: 'percent' | 'flat' | null
  serviceFeeValue?: number | null
  processingFeePercent?: number | null
  processingFeeFixed?: number | null
  taxRatePercent?: number | null
}) => api.patch('/api/admin/platform-fee-settings', data)

// ── Task reach + escalation (category) ───────────────────────
// Updates the new task reach / availability count / escalation
// fields on a category. Uses the same PATCH /categories/:id
// endpoint as updateCategoryApi — kept separate so the type
// signature is clear and the two concerns don't merge into one
// giant object. The backend's updateCategory allowed list accepts
// all of these and records them in adminOverriddenFields.
// Now sent to the dedicated PATCH /categories/:id/reach route (see
// header) — same validation, called directly.
export const updateCategoryReachApi = (
  categoryId: string,
  data: {
    taskReach?:                  'local' | 'radius' | 'global'
    maxTaskRadiusKm?:            number | null
    allowCustomerToExpandReach?: boolean
    showAvailabilityCount?:      'none' | 'total' | 'online'
    escalationEnabled?:          boolean
    escalationStep1Minutes?:     number | null
    escalationStep2Minutes?:     number | null
    escalationStep3Minutes?:     number | null
  }
) => api.patch(`/api/admin/categories/${seg(categoryId)}/reach`, data)

// ── Tasker breakdown by location ──────────────────────────────
// Returns approved expert counts grouped by country → province → city.
// Optional ?categoryId=<id> to narrow to one category.
export const getTaskerBreakdownApi = (params?: { categoryId?: string }) =>
  api.get('/api/admin/tasker-breakdown', { params })

// ── Geography drill-down — experts + customers in a city ──────
// Called by GeographyPage when admin taps into country/province/city.
// Note: axios leaves null params out of the query string entirely, so
// null means "no filter", not "unknown" — GeographyPage sends
// '__unknown__' (GEO_UNKNOWN) to ask for blank/missing values.
export const getGeographyDataApi = (params: {
  country?:  string | null
  province?: string | null
  city?:     string | null
  page?:     number
  limit?:    number
}) => api.get('/api/admin/geography', { params })
// ── Advertisements ────────────────────────────────────────────

// Media upload — quarantine/scanner flow. Replaces the old direct-
// to-Firebase upload (uploadBytesResumable straight from the browser)
// AdvertisementsPage.tsx used to do on its own: the browser now PUTs
// to the short-lived `uploadUrl` this returns (a QUARANTINE object,
// not the final ad bucket — see admin.service.js's
// generateAdMediaUploadUrl), sending `requiredHeaders` unchanged on
// that PUT, then calls confirmAdMediaUploadApi once the PUT succeeds.
export const generateAdMediaUploadUrlApi = (data: { fileName: string; mimeType: string }) =>
  api.post('/api/admin/advertisements/upload-url', data)

// Waits (server-side, bounded) for the scanner to clear the object
// PUT via generateAdMediaUploadUrlApi's uploadUrl, then returns the
// real { mediaUrl, mediaType } pair to pass into createAdvertisementApi
// / updateAdvertisementApi below. A 409 here means the scan is still
// running — safe to retry after a short delay.
export const confirmAdMediaUploadApi = (objectKey: string) =>
  api.post('/api/admin/advertisements/upload-url/confirm', { objectKey })

export const listAdvertisementsApi = (params?: { isActive?: boolean }) =>
  api.get('/api/admin/advertisements', { params })

export const createAdvertisementApi = (data: {
  adType:          'internal' | 'external'
  // Internal
  expertId?:       string | null
  // External
  externalName?:   string | null
  externalPhone?:  string | null
  externalLink?:   string | null
  externalNote?:   string | null
  // Shared
  categoryId?:     string | null
  title?:          string | null
  caption?:        string | null
  mediaUrl:        string
  mediaType:       'image' | 'video'
  isActive?:       boolean
  startDate?:      string | null
  endDate?:        string | null
}) => api.post('/api/admin/advertisements', data)

export const updateAdvertisementApi = (id: string, data: {
  adType?:         'internal' | 'external'
  expertId?:       string | null
  externalName?:   string | null
  externalPhone?:  string | null
  externalLink?:   string | null
  externalNote?:   string | null
  title?:          string | null
  caption?:        string | null
  mediaUrl?:       string
  mediaType?:      'image' | 'video'
  isActive?:       boolean
  categoryId?:     string | null
  startDate?:      string | null
  endDate?:        string | null
}) => api.patch(`/api/admin/advertisements/${seg(id)}`, data)

export const deleteAdvertisementApi = (id: string) =>
  api.delete(`/api/admin/advertisements/${seg(id)}`)

// Public — no auth — used by customer app
export const listActiveAdvertisementsApi = () =>
  api.get('/api/admin/public/advertisements')

// ── Upload tickets (quarantine/scanner pipeline) ───────────────
// FIX (found via frontend review): rescanUploadTicketApi never
// existed here, even though the backend has worked end-to-end since
// the scanner migration — an admin's only signal that a file needed
// this was the TEXT of a Slack message (recovery.service.js's
// sendScanFailedDigest), naming a fileId with nowhere in the
// dashboard to act on it.
export const listScanFailedUploadTicketsApi = (params?: { page?: number; limit?: number }) =>
  api.get('/api/admin/upload-tickets/scan-failed', { params })

export const rescanUploadTicketApi = (fileId: string) =>
  api.post(`/api/admin/upload-tickets/${seg(fileId)}/rescan`, undefined, { timeout: RESCAN_TIMEOUT_MS })

// ── Spotlights ────────────────────────────────────────────────
export const listSpotlightsApi = () =>
  api.get('/api/admin/spotlights')

export const createSpotlightApi = (data: {
  rule:        'top_rated' | 'most_booked' | 'newest_approved' | 'most_responsive'
  categoryId?: string | null
  isActive?:   boolean
}) => api.post('/api/admin/spotlights', data)

export const updateSpotlightApi = (id: string, data: {
  isActive?:   boolean
  categoryId?: string | null
  rule?:       'top_rated' | 'most_booked' | 'newest_approved' | 'most_responsive'
}) => api.patch(`/api/admin/spotlights/${seg(id)}`, data)

export const deleteSpotlightApi = (id: string) =>
  api.delete(`/api/admin/spotlights/${seg(id)}`)
// ── Insurance review list (the "Insurance" tab) ───────────────
// Uploaded insurance documents, mandatory and optional, with their review
// state and what each one means for the tasker's insurance fee. Reviewing
// uses the existing verify call; this never changes who is approved.
export type InsuranceReviewParams = {
  page?: number; limit?: number
  status?: 'pending' | 'accepted' | 'rejected' | 'all'
  search?: string
}

export const listInsuranceReviewsApi = (params?: InsuranceReviewParams) =>
  api.get('/api/admin/insurance', { params })

export const getInsurerStatusApi = () => api.get('/api/admin/insurer/status')
export const listInsurerPoliciesApi = (params?: { page?: number; limit?: number; status?: string; taskId?: string }) =>
  api.get('/api/admin/insurer/policies', { params })
export const retryInsurerPolicyApi = (id: string) => api.post(`/api/admin/insurer/policies/${id}/retry`)